/** @jsxImportSource react */
import clsxm from '@common/lib/clsxm';
import * as React from 'react';

const SegmentedOption: React.FC<{
  value: string;
  name: string;
  isChecked: boolean;
  onClick: (value: string) => void;
  disabled: boolean;
  label: string;
}> = ({ name, value, isChecked = false, onClick, disabled, label }) => {
  return (
    <label
      className={`z-10 flex flex-1 cursor-pointer items-center justify-center rounded py-2 text-sm ${
        disabled
          ? 'opacity-25'
          : isChecked
          ? 'font-medium text-viridian-base'
          : 'text-slate-lighter-40'
      }`}
    >
      <input
        type='radio'
        checked={isChecked}
        name={name}
        value={value}
        id={value}
        className='appearance-none'
        onClick={() => onClick(value)}
        disabled={disabled}
        readOnly
        data-tp-id='gen-input-94deb7ec-97cc-425a-912c-4c1aa5405690'
      />
      {label}
    </label>
  );
};

const Segmented: React.FC<{
  options?: string[] | { value: string; label: string }[];
  width?: number;
  height?: number;
  name: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({
  options = ['On', 'Auto', 'Off'],
  name,
  value,
  disabled = false,
  onChange,
}) => {
  const optionsArray = options.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option
  );

  const valueStringArray = optionsArray.map((option) => option.value);

  const [active, setActive] = React.useState(value);
  const selectedItemIndex =
    valueStringArray.indexOf(value) !== -1
      ? valueStringArray.indexOf(value) * 100
      : 0;

  const [index, setIndex] = React.useState(selectedItemIndex);

  const handleClick = (value: string) => {
    setIndex(valueStringArray.indexOf(value) * 100);
    setActive(value);
    if (onChange) onChange(value);
  };

  return (
    <div
      className='relative rounded border border-slate-lighter-90 p-1 text-center'
      data-tp-id='gen-div-84633b70-7871-4364-a9f0-b1456469fd38'
    >
      <span
        className={clsxm(
          'relative flex items-stretch rounded',
          disabled && 'bg-white'
        )}
        data-tp-id='gen-span-e98d89df-1b16-419b-aff7-3a11b78d6faa'
      >
        {optionsArray.map((option, index) => (
          <SegmentedOption
            key={index}
            label={option.label}
            value={option.value}
            isChecked={!!(option.value === active)}
            name={name}
            onClick={handleClick}
            disabled={disabled}
          />
        ))}
        {active && (
          <span
            className='absolute top-0 h-full rounded bg-viridian-lighter-90'
            style={{
              width: `${100 / options.length}%`,
              transform: `translateX(${index}%)`,
              transition: 'all .25s ease',
            }}
          />
        )}
      </span>
    </div>
  );
};

export default Segmented;
